import { Button } from "antd";
import {
  ArrowRight,
  BarChart3,
  Lock,
  Smartphone,
  TrendingUp,
  Users,
  Zap,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { COUNTRY_CODE } from "../types/types";
import { getCurrencySymbol } from "../utils/functions";

export default function LandingPage() {
  const [country, setCountry] = useState<COUNTRY_CODE>();
  const locale = window.navigator.language;

  useEffect(() => {
    setCountry(findCountryFromLocale(locale));
  }, [locale]);

  const pricing = useMemo(() => findPricesForCountry(country), [country]);
  const currency = useMemo(() => getCurrencySymbol(country), [country]);

  function findCountryFromLocale(locale: string): COUNTRY_CODE | undefined {
    // Brazil
    if (locale === "pt-BR") {
      return COUNTRY_CODE.BRAZIL;
    }
    // Canada
    else if (locale === "en-CA" || locale === "fr-CA") {
      return COUNTRY_CODE.CANADA;
    } else if (locale === "fr-FR") {
      return COUNTRY_CODE.FRANCE;
    }
    // india
    else if (locale.includes("-IN")) {
      return COUNTRY_CODE.INDIA;
    } // indonesia
    else if (locale === "id-ID") {
      return COUNTRY_CODE.INDONESIA;
    } // UK
    else if (locale === "en-GB") {
      return COUNTRY_CODE.UK;
    } else if (locale === "en-US" || "es-US") {
      return COUNTRY_CODE.USA;
    }
  }

  function findPricesForCountry(
    country?: COUNTRY_CODE
  ): IPricingTier | undefined {
    // Brazil
    if (country === COUNTRY_CODE.BRAZIL) {
      return {
        basePrice: "199",
        restaurantPrice: "1,663",
        kiosks: "499",
      };
    }
    // Canada
    else if (country === COUNTRY_CODE.CANADA) {
      return {
        basePrice: "99",
        restaurantPrice: "399",
        inventory: "99",
        kiosks: "99",
      };
    } else if (country === COUNTRY_CODE.FRANCE) {
      return {
        basePrice: "39",
        restaurantPrice: "129",
        kiosks: "59",
      };
    }
    // india
    else if (country === COUNTRY_CODE.INDIA) {
      return {
        basePrice: "2,999",
        restaurantPrice: "9,999",
        doh: "1,999",
        kiosks: "4,999",
      };
    } // indonesia
    else if (country === COUNTRY_CODE.INDONESIA) {
      return {
        basePrice: "599,000",
        restaurantPrice: "4,891,580",
        kiosks: "1,499,000",
      };
    } // UK
    else if (country === COUNTRY_CODE.UK) {
      return {
        basePrice: "49",
        restaurantPrice: "220",
        doh: "99",
        inventory: "99",
        kiosks: "99",
      };
    } else if (country === COUNTRY_CODE.USA) {
      return {
        basePrice: "29",
        restaurantPrice: "199",
        doh: "99",
        inventory: "99",
        kiosks: "99",
      };
    }
  }

  interface IPricingTier {
    basePrice: string;
    restaurantPrice: string;
    doh?: string;
    inventory?: string;
    kiosks?: string;
  }

  function findAnnualSavingsForCountry(country?: COUNTRY_CODE): string {
    // Brazil
    if (country === COUNTRY_CODE.BRAZIL) {
      return `${getCurrencySymbol(country)} 30k`;
    }
    // Canada
    else if (country === COUNTRY_CODE.CANADA) {
      return `${getCurrencySymbol(country)} 45k`;
    } else if (country === COUNTRY_CODE.FRANCE) {
      return `${getCurrencySymbol(country)} 40k`;
    }
    // india
    else if (country === COUNTRY_CODE.INDIA) {
      return `${getCurrencySymbol(country)} 2Lakhs`;
    } // indonesia
    else if (country === COUNTRY_CODE.INDONESIA) {
      return `${getCurrencySymbol(country)} 150M`;
    } // UK
    else if (country === COUNTRY_CODE.UK) {
      return `${getCurrencySymbol(country)} 50k`;
    }
    return `$58k`;
  }

  return (
    <div className="min-h-screen bg-white">
      <section className="pt-32 pb-20 px-6 bg-gradient-to-br from-blue-50 via-white to-purple-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-5xl md:text-6xl font-bold text-gray-900 leading-tight mb-6">
                Transform Your{" "}
                <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  Point of Sale
                </span>
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Modern POS software that streamlines checkout, inventory, and
                customer management. Built for fast-growing retail and
                hospitality businesses.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="https://www.expensehut.com/register"
                  className="inline-flex items-center justify-center 
                      p-2
                      rounded-md 
                      font-medium 
                      text-white 
                      bg-blue-600 
                      hover:bg-blue-700 
                      active:bg-blue-800 
                      transition-colors duration-200
                                            my-2
                      no-underline"
                >
                  Start Free Trial <ArrowRight className="ml-2 w-4 h-4" />
                </a>
                <a
                  href="https://www.youtube.com/watch?v=i5nVSX0kOuk"
                  target="_blank"
                  className="inline-block text-blue-600 hover:underline mt-4 text-underline"
                >
                  Watch Demo
                </a>
              </div>
              <p className="text-sm text-gray-500 mt-6">
                No credit card required. 14-day free trial.
              </p>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-purple-600/20 rounded-3xl blur-3xl"></div>
              <div className="relative rounded-3xl overflow-hidden shadow-2xl h-96">
                <video
                  src="assets/videos/landing-page-kiosks.mp4"
                  autoPlay
                  loop
                  muted
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-green-900/20"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Powerful Features
            </h2>
            <p className="text-xl text-gray-600">
              Everything you need to run your business efficiently
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="p-8 bg-gray-50 rounded-2xl hover:shadow-lg transition">
              <div className="w-14 h-14 bg-blue-100 rounded-lg flex items-center justify-center mb-6">
                <Zap className="w-7 h-7 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Lightning Fast Checkout
              </h3>
              <p className="text-gray-600">
                Process transactions in seconds with our optimized payment
                system. Support all major payment methods.
              </p>
            </div>

            <div className="p-8 bg-gray-50 rounded-2xl hover:shadow-lg transition">
              <div className="w-14 h-14 bg-purple-100 rounded-lg flex items-center justify-center mb-6">
                <BarChart3 className="w-7 h-7 text-purple-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Real-time Analytics
              </h3>
              <p className="text-gray-600">
                Get instant insights into sales trends, customer behavior, and
                inventory levels with advanced reporting.
              </p>
            </div>

            <div className="p-8 bg-gray-50 rounded-2xl hover:shadow-lg transition">
              <div className="w-14 h-14 bg-pink-100 rounded-lg flex items-center justify-center mb-6">
                <Users className="w-7 h-7 text-pink-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Customer Management
              </h3>
              <p className="text-gray-600">
                Build loyalty with customer profiles, purchase history, and
                targeted promotions.
              </p>
            </div>

            <div className="p-8 bg-gray-50 rounded-2xl hover:shadow-lg transition">
              <div className="w-14 h-14 bg-green-100 rounded-lg flex items-center justify-center mb-6">
                <Smartphone className="w-7 h-7 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Mobile Ready
              </h3>
              <p className="text-gray-600">
                Manage your business from anywhere with full-featured iOS and
                Android apps.
              </p>
            </div>

            <div className="p-8 bg-gray-50 rounded-2xl hover:shadow-lg transition">
              <div className="w-14 h-14 bg-orange-100 rounded-lg flex items-center justify-center mb-6">
                <TrendingUp className="w-7 h-7 text-orange-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Inventory Sync
              </h3>
              <p className="text-gray-600">
                Automatic inventory tracking across multiple locations with low
                stock alerts.
              </p>
            </div>

            <div className="p-8 bg-gray-50 rounded-2xl hover:shadow-lg transition">
              <div className="w-14 h-14 bg-cyan-100 rounded-lg flex items-center justify-center mb-6">
                <Lock className="w-7 h-7 text-cyan-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Enterprise Security
              </h3>
              <p className="text-gray-600">
                All data stored in secured systems based on industry standards.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="benefits" className="py-20 px-6 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Why Choose ExpenseHut
            </h2>
            <p className="text-xl text-gray-600">
              Available in 7 countries worldwide.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                All Types Of Businesses
              </h3>
              <ul className="space-y-4">
                <li className="flex gap-3">
                  <span className="text-green-600 font-bold">✓</span>
                  <span className="text-gray-600">No Hardware Required.</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-green-600 font-bold">✓</span>
                  <span className="text-gray-600">
                    Access anywhere - Web, Android or iOS.
                  </span>
                </li>
                <li className="flex gap-3">
                  <span className="text-green-600 font-bold">✓</span>
                  <span className="text-gray-600">99.9% uptime guarantee</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-green-600 font-bold">✓</span>
                  <span className="text-gray-600">24/7 dedicated support</span>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                Cost Savings
              </h3>
              <div className="space-y-6">
                <div className="p-4 bg-white rounded-lg border border-gray-200">
                  <p className="text-3xl font-bold text-blue-600 mb-1">
                    {findAnnualSavingsForCountry(country)}
                  </p>
                  <p className="text-gray-600">
                    Savings From Labor Costs.{" "}
                    <a
                      href="https://expensehut.com/pricing#calculate-savings"
                      className="text-blue-600 hover:underline"
                    >
                      Watch How
                    </a>{" "}
                  </p>
                </div>
                <div className="p-4 bg-white rounded-lg border border-gray-200">
                  <p className="text-3xl font-bold text-purple-600 mb-1">3x</p>
                  <p className="text-gray-600">
                    Return on investment in first year
                  </p>
                </div>
                <div className="p-4 bg-white rounded-lg border border-gray-200">
                  <p className="text-3xl font-bold text-pink-600 mb-1">60%</p>
                  <p className="text-gray-600">Reduction in inventory errors</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to Transform Your POS?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Join us to streamline your operations and boost sales.
          </p>
          <Button
            size="large"
            className="bg-white text-blue-600 hover:bg-gray-100"
          >
            Start Your Free 30-Day Trial <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
        </div>
      </section>
    </div>
  );
}
